/**
 * Main App Component
 *
 * Polygraf Online - Accessibility system for MUNI
 * Refined Academic Design
 */

import {
	Home,
	Layers,
	LogOut,
	Menu,
	Radio,
	ScrollText,
	Settings,
	User,
	Users,
	X,
} from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Link,
	Navigate,
	NavLink,
	Route,
	Routes,
	useLocation,
	useSearchParams,
} from 'react-router-dom'
// Components
import { LanguageSwitcher } from './components/language-switcher'
import { LegalLinks } from './components/legal-links'
import { buttonClasses, cn } from './components/ui'
import { useDismissableMenu } from './hooks/use-dismissable-menu'
import { useUserRole } from './hooks/use-user-role'
// Auth & Roles
import { startAppThemeSync } from './lib/app-theme'
import { signOut, useSession } from './lib/auth-client'
import AdminAuditLogPage from './routes/admin-audit-log'
import AdminSessionsPage from './routes/admin-sessions'
import AdminUsersPage from './routes/admin-users'
import AuthPage from './routes/auth'
import DesktopAuthPage from './routes/desktop-auth'
import ForgotPasswordPage from './routes/forgot-password'
// Pages
import HomePage from './routes/home'
import NotFoundPage from './routes/not-found'
import { PrivacyPage } from './routes/privacy'
import ProfilePage from './routes/profile'
import ResetPasswordPage from './routes/reset-password'
import RoomPage from './routes/room'
import ScreenViewerPage from './routes/screen-viewer'
import SessionCreatePage from './routes/session-create'
import SessionJoinPage from './routes/session-join'
import SessionPresentPage from './routes/session-present'
// Phase 1 live-session UI (writer create + list + join)
import SessionsPage from './routes/sessions'
import SettingsPage from './routes/settings'
import SpaceDetailPage from './routes/space-detail'
import SpacesPage from './routes/spaces'
import { TermsPage } from './routes/terms'
import TranscriptViewerPage from './routes/transcript-viewer'
import UnifiedViewerPage from './routes/unified-viewer'
// Writer and Broadcaster hidden from navigation - accessible only via room services
import WriterMockPage from './routes/writer'

const navItemKeys = [
	{ to: '/', icon: Home, labelKey: 'nav.home' },
	{ to: '/spaces', icon: Layers, labelKey: 'nav.spaces' },
	{ to: '/sessions', icon: Radio, labelKey: 'nav.sessions' },
]

export default function App() {
	const [sidebarOpen, setSidebarOpen] = useState(false)
	const location = useLocation()
	const { data: session, isPending: isSessionLoading } = useSession()
	const { role, isAdmin, isLoading: isRoleLoading } = useUserRole()
	const userMenu = useDismissableMenu()
	const { t } = useTranslation()

	// Apply persisted theme on mount. The Settings page writes `theme:
	// 'light' | 'dark' | 'system'` into `polygraf_app_settings` (localStorage,
	// its `SETTINGS_KEY`) and toggles `data-theme` on documentElement. But App
	// also runs on every page load before Settings ever mounts — without
	// reading the saved value here, every reload reverted the page to the light
	// `polygraf` theme regardless of what the user had picked (QA bug MP-148).
	// The key MUST match Settings' `SETTINGS_KEY` exactly — the original MP-148
	// fix read `polygraf-settings` while Settings writes `polygraf_app_settings`,
	// so the read always missed and the theme never persisted ("stále nejde",
	// QA 2026-05-28). Falls back to `prefers-color-scheme` for `system` and to
	// light for unset / bad JSON on the web; in the apps' WebView unset follows
	// the system (lib/app-theme.ts), and a device scheme change is picked up live.
	useEffect(() => startAppThemeSync(), [])

	const handleSignOut = async () => {
		// Tell the auth page to discard any `?returnTo=` that AuthGuard
		// might attach after the session goes invalid (MP-169). Without
		// this, useSession() flips to `null` between `await signOut()`
		// resolving and the page navigating to '/'; AuthGuard races in,
		// rewrites the URL to `/auth?returnTo=<previous-page>`, and the
		// next user that signs in lands on the *previous* user's page.
		// The flag is one-shot and self-consuming in getSafeReturnTo().
		try {
			sessionStorage.setItem('polygraf:just-signed-out', '1')
		} catch {
			// sessionStorage unavailable (privacy mode) — fall back to the
			// existing redirect-to-'/' below; worst case the bug recurs in
			// that mode, no regression.
		}
		try {
			await signOut()
		} catch (err) {
			console.error('Sign out failed:', err)
		} finally {
			// Force a full reload to the home page so React Query, Sentry
			// scope and any open WebSockets all drop the old session.
			// Mirrors the pattern in routes/profile.tsx after account deletion.
			window.location.href = '/'
		}
	}

	// Check if we're on a viewer page (full-screen mode) or auth page (has own layout)
	const isViewerPage = location.pathname.startsWith('/view/')
	const isJoinPage = location.pathname.startsWith('/join/')
	// The presenter page hosts broadcasts started from a room; underlining
	// "Quick services" there misled the MU reviewer (26. 8. 2026). No nav
	// item is active on it. /sessions and /sessions/create keep theirs.
	const isPresentPage = /^\/sessions\/[^/]+\/present/.test(location.pathname)
	const isNavItemActive = (to: string, isActive: boolean) =>
		isActive && !(to === '/sessions' && isPresentPage)
	const isAuthPage = location.pathname === '/auth'
	const isForgotPasswordPage = location.pathname === '/auth/forgot-password'
	const isResetPasswordPage = location.pathname === '/auth/reset-password'
	const isDesktopAuthPage = location.pathname === '/auth/desktop'
	// Public, no-chrome legal pages — privacy is referenced from SAML SP
	// metadata (PrivacyStatementURL) and required by REFEDS Code of Conduct
	// v2; terms is linked from the app and auth footers.
	const isPrivacyPage = location.pathname === '/privacy' || location.pathname === '/cs/privacy'
	const isTermsPage = location.pathname === '/terms' || location.pathname === '/cs/terms'

	if (isPrivacyPage || isTermsPage) {
		return (
			<Routes>
				<Route path="/privacy" element={<PrivacyPage />} />
				<Route path="/cs/privacy" element={<PrivacyPage />} />
				<Route path="/terms" element={<TermsPage />} />
				<Route path="/cs/terms" element={<TermsPage />} />
			</Routes>
		)
	}

	if (isViewerPage) {
		return (
			<div className="min-h-screen bg-base-200">
				<Routes>
					<Route path="/view/transcript/:sourceId" element={<TranscriptViewerPage />} />
					<Route path="/view/screen/:sourceId" element={<ScreenViewerPage />} />
					<Route path="/view/session/:sessionId" element={<UnifiedViewerPage />} />
				</Routes>
			</div>
		)
	}

	// Phase 1: public session-join landing — full-screen, no app chrome.
	if (isJoinPage) {
		return (
			<Routes>
				<Route path="/join/:sessionId" element={<SessionJoinPage />} />
			</Routes>
		)
	}

	if (isDesktopAuthPage) {
		return (
			<Routes>
				<Route path="/auth/desktop" element={<DesktopAuthPage />} />
			</Routes>
		)
	}

	if (isAuthPage || isForgotPasswordPage || isResetPasswordPage) {
		return (
			<Routes>
				<Route path="/auth" element={<AuthPage />} />
				<Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
				<Route path="/auth/reset-password" element={<ResetPasswordPage />} />
			</Routes>
		)
	}

	return (
		<div className="min-h-screen flex flex-col bg-base-200">
			{/* Skip Navigation Link - WCAG 2.1 AA */}
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-content focus:rounded-lg focus:shadow-soft"
			>
				{t('nav.skipToContent')}
			</a>

			{/* Elegant Header */}
			<header className="bg-base-100 border-b border-base-300 sticky top-0 z-40">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center justify-between h-16">
						{/* Logo & Mobile Menu Toggle */}
						<div className="flex items-center gap-4">
							<button
								type="button"
								className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'lg:hidden p-2')}
								onClick={() => setSidebarOpen(!sidebarOpen)}
								aria-label={t('nav.openMenu')}
							>
								{sidebarOpen ? <X size={22} /> : <Menu size={22} />}
							</button>
							<Link
								to="/"
								className="flex items-center gap-2 group"
								aria-label={t('nav.brandHome')}
							>
								<img
									src="/apple-touch-icon.png"
									alt=""
									aria-hidden="true"
									className="w-9 h-9 rounded-xl shadow-soft group-hover:shadow-soft-lg transition-shadow"
								/>
								<div className="hidden sm:block" aria-hidden="true">
									<span className="font-display text-xl text-base-content">Polygraf</span>
									<span className="font-display text-xl text-coral-700 ml-1">Online</span>
								</div>
							</Link>
						</div>

						{/* Desktop Navigation */}
						<nav className="hidden lg:flex items-center gap-1">
							{navItemKeys.map((item) => (
								<NavLink
									key={item.to}
									to={item.to}
									end={item.to === '/'}
									className={({ isActive }) =>
										`nav-link ${isNavItemActive(item.to, isActive) ? 'active' : ''}`
									}
								>
									{t(item.labelKey)}
								</NavLink>
							))}
						</nav>

						{/* Right Actions */}
						<div className="flex items-center gap-3">
							<LanguageSwitcher />
							<NavLink
								to="/settings"
								className={({ isActive }) =>
									cn(
										buttonClasses({ variant: 'ghost', size: 'sm' }),
										'p-2 rounded-xl',
										isActive && 'bg-base-200',
									)
								}
								aria-label={t('nav.settings')}
							>
								<Settings size={20} />
							</NavLink>
							{isSessionLoading ? (
								<div className="w-8 h-8 rounded-full bg-base-200 animate-pulse" />
							) : session?.user ? (
								<div ref={userMenu.containerRef} className="relative">
									<button
										type="button"
										ref={userMenu.triggerRef}
										className={cn(
											buttonClasses({ variant: 'ghost', size: 'sm' }),
											'p-2 rounded-xl flex items-center gap-2',
										)}
										// F-008: include visible name in the accessible name so axe's
										// label-content-name-mismatch rule passes. The visible <span>
										// inside renders user.name; the accessible name now begins with
										// that text and is followed by the menu purpose.
										aria-label={`${session.user.name || session.user.email?.split('@')[0] || ''}, ${t('nav.userMenu')}`.trim()}
										{...userMenu.triggerProps}
									>
										{session.user.image ? (
											<img src={session.user.image} alt="" className="w-8 h-8 rounded-full" />
										) : (
											<div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
												<User size={16} className="text-primary" />
											</div>
										)}
										<span className="hidden sm:inline text-sm font-medium">
											{session.user.name || session.user.email?.split('@')[0]}
										</span>
										{role !== 'USER' && (
											<span
												className={`hidden sm:inline px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${role === 'ADMIN' ? 'bg-error/15 text-coral-700' : 'bg-accent/15 text-coral-700'}`}
											>
												{t(`roles.${role.toLowerCase()}`)}
											</span>
										)}
									</button>
									{userMenu.open && (
										<div className="absolute right-0 top-full z-50 w-56 mt-2 overflow-hidden rounded-xl bg-base-100 shadow-soft-lg">
											{/* Identity — presentational header, lifted out of the
											    menu so DaisyUI's grid-auto-flow doesn't lay the name
											    and email out side-by-side. Aligned to the 20px icon
											    rail (px-5 = menu padding .5rem + item padding .75rem). */}
											<div className="px-5 pt-3 pb-2.5">
												<p className="text-sm font-semibold text-base-content leading-tight truncate">
													{session.user.name || session.user.email?.split('@')[0]}
												</p>
												<p className="text-xs text-base-content/50 leading-tight truncate mt-0.5">
													{session.user.email}
												</p>
												{/* The trigger's role badge is `hidden sm:inline` and is
												    suppressed entirely for plain USERs, so on a phone the
												    role was shown nowhere (QA 17. 9. 2026). Repeat it here,
												    unconditionally, where the panel is the same width at
												    every breakpoint. Held back until the role query settles
												    so an ADMIN is never briefly labelled "User". */}
												{!isRoleLoading && (
													<span
														className={`inline-block mt-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${role === 'ADMIN' ? 'bg-error/15 text-coral-700' : role === 'PRESENTER' ? 'bg-accent/15 text-coral-700' : 'bg-base-content/10 text-base-content/70'}`}
													>
														{t(`roles.${role.toLowerCase()}`)}
													</span>
												)}
											</div>
											<div className="border-t border-base-content/10" />
											<ul className="menu w-full p-2">
												<li>
													<Link
														to="/profile"
														className="flex items-center gap-3"
														onClick={userMenu.close}
													>
														<User size={16} />
														{t('common.profile')}
													</Link>
												</li>
												{isAdmin && (
													<>
														<li>
															<Link
																to="/admin/users"
																className="flex items-center gap-3"
																onClick={userMenu.close}
															>
																<Users size={16} />
																{t('nav.adminUsers')}
															</Link>
														</li>
														<li>
															<Link
																to="/admin/sessions"
																className="flex items-center gap-3"
																onClick={userMenu.close}
															>
																<Radio size={16} />
																{t('nav.adminSessions')}
															</Link>
														</li>
														<li>
															<Link
																to="/admin/audit-log"
																className="flex items-center gap-3"
																onClick={userMenu.close}
															>
																<ScrollText size={16} />
																{t('nav.adminAuditLog')}
															</Link>
														</li>
													</>
												)}
											</ul>
											<div className="border-t border-base-content/10" />
											<ul className="menu w-full p-2">
												<li>
													<button
														type="button"
														onClick={() => {
															userMenu.close()
															void handleSignOut()
														}}
														className="flex items-center gap-3 text-error hover:bg-error/10"
													>
														<LogOut size={16} />
														{t('common.logout')}
													</button>
												</li>
											</ul>
										</div>
									)}
								</div>
							) : (
								<>
									<Link
										to="/auth"
										className={cn(buttonClasses(), 'hidden sm:flex items-center gap-2')}
									>
										<User size={16} />
										<span>{t('common.login')}</span>
									</Link>
									<Link
										to="/auth"
										className={cn(buttonClasses(), 'sm:hidden p-3')}
										aria-label={t('common.login')}
									>
										<User size={18} />
									</Link>
								</>
							)}
						</div>
					</div>
				</div>
			</header>

			<div className="flex flex-1">
				{/* Mobile sidebar overlay */}
				{sidebarOpen && (
					<div className="lg:hidden fixed inset-0 z-50">
						<div
							className="absolute inset-0 bg-black/40 backdrop-blur-sm"
							onClick={() => setSidebarOpen(false)}
						/>
						<aside className="absolute left-0 top-0 h-full w-72 bg-base-100 shadow-soft-lg flex flex-col animate-slide-in-right">
							<div className="p-4 border-b border-base-300 flex items-center justify-between">
								<div className="flex items-center gap-2">
									<img
										src="/apple-touch-icon.png"
										alt=""
										aria-hidden="true"
										className="w-8 h-8 rounded-xl"
									/>
									<span className="font-display text-lg">{t('nav.menu')}</span>
								</div>
								<button
									type="button"
									className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2')}
									onClick={() => setSidebarOpen(false)}
									aria-label={t('common.close')}
								>
									<X size={20} />
								</button>
							</div>
							<nav className="flex-1 p-4">
								<div className="space-y-1">
									{navItemKeys.map((item) => (
										<NavLink
											key={item.to}
											to={item.to}
											end={item.to === '/'}
											onClick={() => setSidebarOpen(false)}
											className={({ isActive }) =>
												`sidebar-item ${isNavItemActive(item.to, isActive) ? 'active' : ''}`
											}
										>
											<item.icon size={20} />
											{t(item.labelKey)}
										</NavLink>
									))}
									{isAdmin && (
										<>
											<div className="pt-3 pb-1 px-3 text-xs uppercase tracking-wide text-base-content/50">
												{t('nav.adminSection')}
											</div>
											<NavLink
												to="/admin/users"
												onClick={() => setSidebarOpen(false)}
												className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
											>
												<Users size={20} />
												{t('nav.adminUsers')}
											</NavLink>
											<NavLink
												to="/admin/sessions"
												onClick={() => setSidebarOpen(false)}
												className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
											>
												<Radio size={20} />
												{t('nav.adminSessions')}
											</NavLink>
											<NavLink
												to="/admin/audit-log"
												onClick={() => setSidebarOpen(false)}
												className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
											>
												<ScrollText size={20} />
												{t('nav.adminAuditLog')}
											</NavLink>
										</>
									)}
								</div>
							</nav>
							<div className="p-4 border-t border-base-300">
								<NavLink
									to="/settings"
									onClick={() => setSidebarOpen(false)}
									className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
								>
									<Settings size={20} />
									{t('nav.settings')}
								</NavLink>
							</div>
						</aside>
					</div>
				)}

				{/* Main content */}
				<main id="main-content" className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" tabIndex={-1}>
					<div className="max-w-5xl mx-auto">
						<Routes>
							<Route path="/" element={<HomePage />} />
							<Route path="/spaces" element={<SpacesPage />} />
							<Route path="/spaces/:spaceId" element={<SpaceDetailPage />} />
							<Route path="/spaces/:spaceId/rooms/:roomId" element={<RoomPage />} />
							<Route path="/sessions" element={<SessionsPage />} />
							<Route path="/sessions/create" element={<SessionCreatePage />} />
							<Route path="/sessions/:id/present" element={<SessionPresentPage />} />
							<Route path="/settings" element={<SettingsPage />} />
							<Route path="/profile" element={<ProfilePage />} />
							<Route path="/admin/users" element={<AdminUsersPage />} />
							<Route path="/admin/sessions" element={<AdminSessionsPage />} />
							<Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
							<Route path="/writer" element={<WriterGuard />} />
							<Route path="/auth" element={<AuthPage />} />
							<Route path="*" element={<NotFoundPage />} />
						</Routes>
					</div>
				</main>
			</div>

			{/* Elegant Footer */}
			<footer className="bg-base-100 border-t border-base-300 py-4">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-base-content/60">
						<div className="text-center sm:text-left">
							<p>© 2026 Polygraf Online — Masarykova univerzita</p>
							{/* NDI SDK licence requires the trademark notice in the app credits. */}
							<p className="text-xs">{t('footer.ndiTrademark')}</p>
						</div>
						<LegalLinks />
					</div>
				</div>
			</footer>
		</div>
	)
}

// Route guard — only accessible with valid room params (documentId + roomId from room service)
function RoomParamsGuard({ children }: { children: ReactElement }) {
	const [searchParams] = useSearchParams()
	// `has()` returns true for `?documentId=` (empty value) — require non-empty
	// trimmed values so a bookmark with cleared params doesn't slip through.
	const documentId = searchParams.get('documentId')?.trim()
	const roomId = searchParams.get('roomId')?.trim()
	if (!documentId || !roomId) {
		return <Navigate to="/" replace />
	}
	return children
}

function WriterGuard() {
	return (
		<RoomParamsGuard>
			<WriterMockPage />
		</RoomParamsGuard>
	)
}
